<template>
  <!-- La mayoría de atenciones nacen de agendamiento directo, sin origen que mostrar. -->
  <div v-if="origen">
    <div class="flex items-center justify-between my-2">
      <div class="flex items-center">
        <svg-icon type="mdi" :path="mdiSourceBranch" class="size-5 text-blue-600 dark:text-blue-500 mr-2" />
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Origen de la atención</h3>
        <span
          class="ml-3 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
          :class="CHIP_POR_TIPO[origen.tipo]"
        >
          {{ origen.label }}
          <template v-if="origen.ciotTipo && origen.tipo !== 'DEMANDA_INDUCIDA'">
            · {{ origen.ciotTipo }}
          </template>
        </span>
        <span v-if="origen.fecha" class="ml-3 text-sm text-gray-500 dark:text-gray-400">
          {{ formatearFechaEnEspanol(origen.fecha) }}
        </span>
      </div>
      <ButtonDefault
        v-if="origen.tipo === 'DEMANDA_INDUCIDA' ? origen.deinId : origen.ciotId"
        size="xs"
        @click="verDetalles(origen)"
      >
        <EyeIcon class="size-4 mr-1" />
        Ver detalles
      </ButtonDefault>
    </div>

    <!-- El modal va siempre montado; Preline necesita el elemento para engancharse. -->
    <ModalDefault
      id="modal-origen-demanda"
      title="Demanda Inducida que originó la atención"
      size="xl"
      :button="false"
      :fnc-on-close="() => (deinSeleccionada = null)"
    >
      <template #modal-body>
        <DemandaInducidaDetalleView
          v-if="deinSeleccionada"
          :dein-id="deinSeleccionada"
          :is-modal="true"
          :readonly="true"
        />
      </template>
    </ModalDefault>

    <ModalDefault
      id="modal-origen-orden"
      title="Orden que originó la atención"
      size="xl"
      :button="false"
      :fnc-on-close="() => (ciotSeleccionada = null)"
    >
      <template #modal-body>
        <OrdenesView v-if="ciotSeleccionada" :ciot-id="ciotSeleccionada" :is-modal="true" />
      </template>
    </ModalDefault>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, watch } from 'vue'
import { EyeIcon } from '@heroicons/vue/24/outline'
import { mdiSourceBranch } from '@mdi/js'
import ModalDefault from '@/modules/Core/components/Layout/ModalDefault.vue'
import { ordenesTecnologiaService } from '@/services'
import { formatearFechaEnEspanol, openModal } from '@/modules/Core/usings/generales'
import type { IOrigenAtencion, TipoOrigenAtencion } from '@/modules/Asistencial/interfaces/IOrigenAtencion'

// No convertir a import estático: PatientInfoCard se registra en plugins/index.ts, y estas
// vistas arrastran módulos que usan Date.prototype antes de que ese archivo lo extienda.
const DemandaInducidaDetalleView = defineAsyncComponent(
  () => import('@/modules/DemandaInducida/views/DemandaInducida/DemandaInducidaDetalleView.vue')
)
const OrdenesView = defineAsyncComponent(
  () => import('@/modules/Admision/views/Ordenes/OrdenesView.vue')
)

const CHIP_POR_TIPO: Record<TipoOrigenAtencion, string> = {
  DEMANDA_INDUCIDA: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  INTERNA: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  EXTERNA: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
}

const props = defineProps<{ cittId?: number | string | null }>()
const emit = defineEmits<{
  cargado: [origen: IOrigenAtencion | null]
}>()

const origen = ref<IOrigenAtencion | null>(null)
const deinSeleccionada = ref<number | null>(null)
const ciotSeleccionada = ref<number | null>(null)

const verDetalles = (o: IOrigenAtencion) => {
  if (o.tipo === 'DEMANDA_INDUCIDA' && o.deinId) {
    deinSeleccionada.value = o.deinId
    openModal('modal-origen-demanda', false)
    return
  }
  ciotSeleccionada.value = o.ciotId
  openModal('modal-origen-orden', false)
}

const cargar = async (cittId?: number | string | null) => {
  origen.value = null
  emit('cargado', null)
  if (!cittId) return
  try {
    const { data } = await ordenesTecnologiaService.getOrigenAtencion(cittId)
    origen.value = data.body ?? null
    emit('cargado', origen.value)
  } catch (error) {
    console.error('Error al cargar el origen de la atención:', error)
  }
}

watch(() => props.cittId, cargar, { immediate: true })
</script>
